import React, { useEffect, useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSearchbar,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import { useHistory } from "react-router";

import { ReembolsoPS } from "src/utils/interfaces";

import ReembolsosAPI from "src/services/reembolsos";

import Header from "src/components/Header";

// Selector simple de PS con buscador remoto (min. 2 caracteres).
const SelectorPS: React.FC<{
  titulo: string;
  seleccionado: ReembolsoPS | null;
  onSeleccionar: (ps: ReembolsoPS | null) => void;
}> = ({ titulo, seleccionado, onSeleccionar }) => {
  const [texto, setTexto] = useState("");
  const [opciones, setOpciones] = useState<ReembolsoPS[]>([]);

  useEffect(() => {
    if (texto.trim().length < 2) {
      setOpciones([]);
      return;
    }
    const timer = setTimeout(() => {
      ReembolsosAPI.buscarPS(texto.trim())
        .then((response) => setOpciones(response.data.ps || []))
        .catch(() => setOpciones([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [texto]);

  if (seleccionado) {
    return (
      <IonItem>
        <IonLabel className="ion-text-wrap">
          <h3>{titulo}</h3>
          <p>{seleccionado.nombre}</p>
        </IonLabel>
        <IonButton slot="end" fill="clear" onClick={() => onSeleccionar(null)}>
          Cambiar
        </IonButton>
      </IonItem>
    );
  }

  return (
    <>
      <IonItem lines="none">
        <IonLabel>
          <h3>{titulo}</h3>
        </IonLabel>
      </IonItem>
      <IonSearchbar
        value={texto}
        placeholder="Buscar punto de servicio..."
        onIonChange={(e) => setTexto(e.detail.value || "")}
      />
      {opciones.length > 0 && (
        <IonList>
          {opciones.map((ps) => (
            <IonItem
              key={ps.id}
              button
              onClick={() => {
                onSeleccionar(ps);
                setTexto("");
                setOpciones([]);
              }}
            >
              <IonLabel className="ion-text-wrap">{ps.nombre}</IonLabel>
            </IonItem>
          ))}
        </IonList>
      )}
    </>
  );
};

const ReembolsoPasajeReclamo: React.FC = () => {
  const [fecha, setFecha] = useState("");
  const [psOrigen, setPsOrigen] = useState<ReembolsoPS | null>(null);
  const [psDestino, setPsDestino] = useState<ReembolsoPS | null>(null);
  const [comentario, setComentario] = useState("");
  // Ventana configurada en EMSYS: periodo para enviar reclamos + fechas reclamables.
  const [reclamoAbierto, setReclamoAbierto] = useState(true);
  const [abiertoHasta, setAbiertoHasta] = useState<string | null>(null);
  const [fechasDesde, setFechasDesde] = useState<string | null>(null);
  const [fechasHasta, setFechasHasta] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [present] = useIonToast();
  const history = useHistory();

  useEffect(() => {
    ReembolsosAPI.getConfig()
      .then((response) => {
        setReclamoAbierto(Boolean(response.data.reclamo_abierto));
        setAbiertoHasta(response.data.reclamo_abierto_hasta || null);
        setFechasDesde(response.data.fechas_reclamables_desde || null);
        setFechasHasta(response.data.fechas_reclamables_hasta || null);
      })
      .catch(() => undefined);
  }, []);

  const hoy = new Date().toISOString().slice(0, 10);
  const maximo = fechasHasta && fechasHasta < hoy ? fechasHasta : hoy;
  const minimo = fechasDesde || undefined;

  const enviar = () => {
    if (!fecha) {
      present({ message: "Indicá la fecha del traslado", duration: 4000, color: "warning" });
      return;
    }
    if (!psOrigen || !psDestino) {
      present({
        message: "Seleccioná el punto de servicio de origen y el de destino",
        duration: 4000,
        color: "warning",
      });
      return;
    }
    if (psOrigen.id === psDestino.id) {
      present({
        message: "El origen y el destino no pueden ser el mismo punto de servicio",
        duration: 4000,
        color: "warning",
      });
      return;
    }

    setEnviando(true);
    ReembolsosAPI.crearReclamo({
      fecha,
      ps_origen_id: psOrigen.id,
      ps_destino_id: psDestino.id,
      comentario: comentario.trim() || undefined,
    })
      .then(() => {
        present({
          message: "Reclamo registrado. Será revisado por el equipo de gestión.",
          duration: 5000,
          color: "success",
        });
        history.replace("/reembolsos-pasajes");
      })
      .catch((error) => {
        const mensaje =
          error?.response?.data?.error || "Ocurrió un error al registrar el reclamo";
        present({ message: mensaje, duration: 5000, color: "danger" });
      })
      .finally(() => setEnviando(false));
  };

  return (
    <IonPage>
      <Header titulo="Reclamar Traslado" />

      <IonContent id="reembolso-pasaje-reclamo">
        <div className="container mb-4">
          {!reclamoAbierto && (
            <IonCard color="light-warning">
              <IonCardContent>
                <p className="ion-text-center">
                  El período para enviar reclamos de pasajes está <strong>cerrado</strong>.
                </p>
              </IonCardContent>
            </IonCard>
          )}

          {reclamoAbierto && (
          <IonCard>
            <IonCardContent>
              <p>
                Si te trasladaste entre dos puntos de servicio y el traslado no aparece en tus
                reembolsos, podés reclamarlo acá.
                {fechasDesde || fechasHasta ? (
                  <>
                    {" "}Podés reclamar traslados con fecha
                    {fechasDesde ? <> desde el <strong>{fechasDesde}</strong></> : null}
                    {fechasHasta ? <> hasta el <strong>{fechasHasta}</strong></> : null}.
                  </>
                ) : null}
                {abiertoHasta ? (
                  <> Los reclamos se reciben hasta el <strong>{abiertoHasta}</strong>.</>
                ) : null}
              </p>

              <IonItem>
                <IonLabel position="stacked">Fecha del traslado</IonLabel>
                <IonInput
                  type="date"
                  value={fecha}
                  min={minimo}
                  max={maximo}
                  onIonChange={(e) => setFecha(e.detail.value || "")}
                />
              </IonItem>

              <SelectorPS
                titulo="Punto de servicio de ORIGEN"
                seleccionado={psOrigen}
                onSeleccionar={setPsOrigen}
              />

              <SelectorPS
                titulo="Punto de servicio de DESTINO"
                seleccionado={psDestino}
                onSeleccionar={setPsDestino}
              />

              <IonItem>
                <IonLabel position="stacked">Comentario (opcional)</IonLabel>
                <IonTextarea
                  value={comentario}
                  rows={3}
                  maxlength={500}
                  placeholder="Contanos lo que pasó..."
                  onIonChange={(e) => setComentario(e.detail.value || "")}
                />
              </IonItem>

              <IonButton
                expand="block"
                color="primary"
                disabled={enviando}
                onClick={enviar}
                style={{ marginTop: "16px" }}
              >
                {enviando ? "Enviando..." : "Enviar reclamo"}
              </IonButton>
            </IonCardContent>
          </IonCard>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ReembolsoPasajeReclamo;
